import React, { ReactElement, useState } from 'react';
import { RequestDto } from '../../../../services/dtos/request.dto';
import { Box, Chip, Grid, Pagination, Paper, Tooltip, Typography } from '@mui/material';

// Define a type for Chip colors
type ChipColor = 'default' | 'success' | 'info' | 'error' | 'primary' | 'secondary' | 'warning' | undefined;

const ITEMS_PER_PAGE = 8;

// Function to validate the color
export function getChipColor(color?: string): ChipColor {
    const allowedColors: ChipColor[] = ['default', 'success', 'info', 'error', 'primary', 'secondary', 'warning'];

    switch (color) {
        case 'En cours':
            color = 'primary';
            break;
        case 'Terminé':
            color = 'success';
            break;
        case 'Abandonnée':
            color = 'error';
            break;
    }

    return allowedColors.includes(color as ChipColor) ? (color as ChipColor) : 'default';
}

const maxCharsPerLine = 55;

/**
 * Renders a UI component to display a list of contract requests.
 *
 * @param {Object} props - The component properties.
 * @param {Array<RequestDto>} props.requests - The array of request objects to display.
 *
 * @returns {ReactElement} - The rendered contract requests UI component.
 */
export default function ContractRequestsUi(props: {
    requests?: Array<RequestDto>;
    onOneContract: boolean;
}): ReactElement {
    const [page, setPage] = useState(1);

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const pagedRequests = props.requests?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    let listRequests = pagedRequests?.map(request => (
        <Grid key={request?.idFlux} item xs={12} md={3}>
            <Paper variant={'outlined'} sx={{ height: '210px', padding: '20px' }}>
                <Grid
                    container
                    sx={{
                        alignContent: 'space-between',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '100%',
                    }}
                >
                    <Grid item xs={12}>
                        {/*Titre + action de la card*/}
                        <Tooltip title={request?.typeFlux} arrow>
                            <Typography variant={'h5'} noWrap>
                                {request?.typeFlux}
                            </Typography>
                        </Tooltip>
                        {!props.onOneContract && (
                            <Typography variant={'body1'} sx={{ color: 'black', fontWeight: 'bold' }}>
                                {request?.referencePdi}
                            </Typography>
                        )}
                        <Typography variant="body1">
                            Créée le {new Date(request?.dateCreation).toLocaleDateString()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {request.detail && request.detail.length > maxCharsPerLine ? (
                            <Tooltip title={request.detail} arrow>
                                <Typography variant={'body1'}>
                                    {request.detail?.substring(0, maxCharsPerLine) + '...'}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography variant={'body1'}>{request.detail}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Chip label={request?.etatFlux} color={getChipColor(request?.etatFlux)} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    ));

    return (
        <>
            {listRequests ? (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {listRequests}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={3} />
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            color={'primary'}
                            showFirstButton
                            showLastButton
                            count={Math.ceil((props.requests?.length || 0) / ITEMS_PER_PAGE)}
                            page={page}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} />
                </Grid>
            ) : (
                <Box sx={{ marginTop: '30px' }}>
                    <Typography sx={{ textAlign: 'center' }} variant={'body1'}>
                        Aucune demande n'a été faite pour ce contrat.
                    </Typography>
                </Box>
            )}
        </>
    );
}
